import type { VariantProps } from "class-variance-authority";
import React from "react";
import { Badge, badgeVariants } from "@components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@components/ui/tooltip";
import { cn } from "@lib/utils";
import { useTranslateProposalFields } from "@features/proposals/hooks/proposal-translate-fields";
import { ActivityMemberRoleType } from "@features/proposals/types/member";

interface MemberRoleBadgeProps {
  role?: ActivityMemberRoleType;
  className?: string;
}

/**
 * Component for displaying an activity member role with a tooltip.
 * Shows a role as a badge with a descriptive tooltip on hover.
 */
export const MemberRoleBadge = ({
  role,
  className,
  ...props
}: MemberRoleBadgeProps & VariantProps<typeof badgeVariants>) => {
  const { activityMember } = useTranslateProposalFields();

  if (!role) {
    return null;
  }

  const roleLabel = activityMember.translateRole(role);
  const roleDescription = activityMember.translateRoleDescription(role);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" {...props} className={cn("cursor-pointer", className)}>
            {roleLabel}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs">{roleDescription}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
